import { ContractTaskStatus } from "../../../autogen/bff-api";
import { isWellFormedId } from "../../../common/ids/isWellFormedId";

export type TaskListFilters = {
  personIds: string[];
  contractIds: string[];
  statuses: string[];
  dueBefore?: string;
  changedSince?: string;
};

const LIST_PARAMS = { personIds: "person", contractIds: "contract", statuses: "status" } as const;

export type TaskListFilter = keyof typeof LIST_PARAMS;

export type UnknownTaskFilterValue = { filter: TaskListFilter; value: string };

const TASK_STATUSES: readonly string[] = Object.values(ContractTaskStatus);

const isTaskStatus = (value: string): boolean => TASK_STATUSES.includes(value);

const IS_KNOWN: Record<TaskListFilter, (value: string) => boolean> = {
  personIds: isWellFormedId,
  contractIds: isWellFormedId,
  statuses: isTaskStatus,
};

const LIST_FILTERS = Object.keys(LIST_PARAMS) as TaskListFilter[];

export const taskFiltersFromAddress = (params: URLSearchParams): TaskListFilters => ({
  personIds: knownValues(params, "personIds"),
  contractIds: knownValues(params, "contractIds"),
  statuses: knownValues(params, "statuses"),
  dueBefore: params.get("dueBefore") ?? undefined,
  changedSince: params.get("changedSince") ?? undefined,
});

export const unknownTaskFilterValues = (params: URLSearchParams): UnknownTaskFilterValue[] =>
  LIST_FILTERS.flatMap((filter) =>
    [...new Set(listParam(params, filter).filter((value) => !IS_KNOWN[filter](value)))].map((value) => ({
      filter,
      value,
    }))
  );

export const withTaskFilters = (params: URLSearchParams, filters: TaskListFilters): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete("personName");
  for (const filter of LIST_FILTERS) {
    const values = filters[filter];
    if (values.length > 0) next.set(LIST_PARAMS[filter], values.join(","));
    else next.delete(LIST_PARAMS[filter]);
  }
  setOrDelete(next, "dueBefore", filters.dueBefore);
  setOrDelete(next, "changedSince", filters.changedSince);
  return next;
};

export const NO_TASK_FILTERS: TaskListFilters = {
  personIds: [],
  contractIds: [],
  statuses: [],
  dueBefore: undefined,
  changedSince: undefined,
};

const knownValues = (params: URLSearchParams, filter: TaskListFilter): string[] =>
  listParam(params, filter).filter(IS_KNOWN[filter]);

const listParam = (params: URLSearchParams, filter: TaskListFilter): string[] =>
  (params.get(LIST_PARAMS[filter]) ?? "").split(",").filter(Boolean);

const setOrDelete = (params: URLSearchParams, name: string, value: string | undefined) => {
  if (value) params.set(name, value);
  else params.delete(name);
};
